import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import client from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const CATEGORIES = ["Dresses", "Skirts", "Tops", "Outerwear", "Head Coverings", "Men"];
const TAGS = ["shabbos", "yom-tov", "long-sleeve", "midi", "high-neck", "layering", "tznius"];
const OCCASION_LABELS = { "shabbos": "Shabbos", "yom-tov": "Yom Tov" };

export default function Shop() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [q, setQ] = useState(searchParams.get("q") || "");
  const [activeTag, setActiveTag] = useState(searchParams.get("tag") || "");
  const [priceRange, setPriceRange] = useState({ min: "", max: "" });

  useEffect(() => { setActiveTag(searchParams.get("tag") || ""); }, [searchParams]);

  useEffect(() => {
    const params = {};
    if (category) params.category = category;
    if (q) params.q = q;
    if (activeTag) params.tag = activeTag;
    if (priceRange.min) params.min_price = priceRange.min;
    if (priceRange.max) params.max_price = priceRange.max;
    client.get("/products", { params }).then(r => setProducts(r.data));
  }, [category, q, activeTag, priceRange]);

  const heading = category ? category : (activeTag && OCCASION_LABELS[activeTag]) ? `Shop ${OCCASION_LABELS[activeTag]}` : "The Full Collection";
  const eyebrow = (activeTag && OCCASION_LABELS[activeTag]) ? "Occasion" : "Shop";

  return (
    <div className="container-editorial mt-8 md:mt-14">
      <div className="border-b border-[#EAE5D9] pb-10 mb-12">
        <p className="label-uppercase text-[#B24C38]">{eyebrow}</p>
        <h1 className="font-serif text-5xl md:text-6xl lg:text-7xl tracking-tighter mt-3" data-testid="shop-title">
          {heading}
        </h1>
        <p className="text-[#4A4A4A] mt-4 max-w-xl">
          Every piece is stocked and shipped by a verified Jewish-owned atelier. Shipping consolidates automatically.
        </p>
      </div>

      <div className="grid grid-cols-12 gap-8">
        <aside className="col-span-12 md:col-span-3">
          <div className="relative mb-8">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#4A4A4A]" />
            <Input data-testid="shop-search-input" value={q} onChange={e => setQ(e.target.value)}
              placeholder="Search products..." className="pl-9 rounded-none border-[#111111] bg-transparent" />
          </div>

          <Accordion type="multiple" defaultValue={["cat","tag","price"]}>
            <AccordionItem value="cat" className="border-b border-[#EAE5D9]">
              <AccordionTrigger className="label-uppercase" data-testid="filter-category-toggle">Category</AccordionTrigger>
              <AccordionContent className="space-y-2 pt-2">
                {CATEGORIES.map(c => (
                  <a key={c} href={`/shop/${c}`} className={`block text-sm ${category===c ? "text-[#B24C38] font-semibold" : ""}`}
                     data-testid={`filter-cat-${c.toLowerCase().replace(/\s+/g,'-')}`}>
                    {c}
                  </a>
                ))}
                <a href="/shop" className="block text-sm">All Categories</a>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="tag" className="border-b border-[#EAE5D9]">
              <AccordionTrigger className="label-uppercase">Occasion & Style</AccordionTrigger>
              <AccordionContent className="flex flex-wrap gap-2 pt-2">
                {TAGS.map(t => (
                  <button key={t} onClick={() => {
                            const next = t === activeTag ? "" : t;
                            setActiveTag(next);
                            const sp = new URLSearchParams(searchParams);
                            if (next) sp.set("tag", next); else sp.delete("tag");
                            setSearchParams(sp, { replace: true });
                          }}
                          data-testid={`filter-tag-${t}`}
                          className={`px-3 py-1 text-xs border ${activeTag===t ? "bg-[#111] text-[#F7F5F0] border-[#111]" : "border-[#111]"}`}>
                    {OCCASION_LABELS[t] || t}
                  </button>
                ))}
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="price" className="border-b border-[#EAE5D9]">
              <AccordionTrigger className="label-uppercase">Price</AccordionTrigger>
              <AccordionContent className="flex gap-2 pt-2">
                <Input type="number" placeholder="Min" value={priceRange.min}
                       onChange={e => setPriceRange(pr => ({...pr, min: e.target.value}))}
                       data-testid="filter-price-min"
                       className="rounded-none border-[#111] bg-transparent" />
                <Input type="number" placeholder="Max" value={priceRange.max}
                       onChange={e => setPriceRange(pr => ({...pr, max: e.target.value}))}
                       data-testid="filter-price-max"
                       className="rounded-none border-[#111] bg-transparent" />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </aside>

        <div className="col-span-12 md:col-span-9">
          <p className="label-uppercase text-[#4A4A4A] mb-6" data-testid="products-count">{products.length} products</p>
          {products.length === 0 ? (
            <p className="text-[#4A4A4A] py-16 text-center">Nothing matches your filters yet.</p>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-14">
              {products.map(p => <ProductCard key={p.id} product={p} testId={`shop-product-${p.id}`} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
